/**
 * IntakeTab Component
 * AIDLC Administrative Backend
 *
 * What the customer asked for. The requirements intake is run in the CRM,
 * with the customer's stakeholders; when it is approved the CRM sends it here
 * and each approved requirement becomes a backlog item with an estimate. This
 * tab shows that packet as it was received (who was asked, what they said,
 * who approved it) and, for each requirement, the estimate beside what the
 * work has cost so far.
 *
 * It is a read-only copy. The intake is changed in the CRM, which sends it
 * again; work already started is never rewritten from there.
 */

import { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { getProjectIntakes } from '../../../services/intakeService';
import type { IntakeStatus, IntakeRequirementRow } from '../../../services/intakeService';

interface IntakeTabProps {
  projectId: string;
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const count = new Intl.NumberFormat('en-US');

const STATUS_LABELS: Record<string, string> = { new: 'Not started', open: 'Open', in_progress: 'In progress', review: 'In review', done: 'Done', closed: 'Closed' };
const ROLE_LABELS: Record<string, string> = { sponsor: 'Sponsor', approver: 'Approver', sme: 'Subject-matter expert', end_user: 'End user', it_security: 'IT / security', procurement: 'Procurement' };

function statusOf(row: IntakeRequirementRow): string {
  return row.withdrawn ? 'Withdrawn in the CRM' : (STATUS_LABELS[row.status] ?? row.status);
}

function Figure({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums text-black dark:text-white">{value}</div>
      {note && <div className="text-[11px] text-gray-500 mt-1">{note}</div>}
    </div>
  );
}

function RequirementsTable({ rows }: { rows: IntakeRequirementRow[] }) {
  if (rows.length === 0) {
    return <div className="px-4 py-6 text-xs text-gray-500">No approved requirement has been sent for this intake yet.</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500">
            <th scope="col" className="text-left px-4 py-2 font-medium">Requirement</th>
            <th scope="col" className="text-left px-4 py-2 font-medium">Status</th>
            <th scope="col" className="text-left px-4 py-2 font-medium">Agent</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Est. tokens</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Est. cost</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Actual tokens</th>
            <th scope="col" className="text-right px-4 py-2 font-medium">Actual cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.itemId} className={`border-t border-zinc-100 dark:border-zinc-800 ${row.withdrawn ? 'text-gray-400' : ''}`}>
              <td className="px-4 py-2">
                <div className="font-medium text-black dark:text-white">{row.title}</div>
                <div className="text-[11px] text-gray-500">{row.itemId}</div>
              </td>
              <td className="px-4 py-2">{statusOf(row)}</td>
              <td className="px-4 py-2">{row.assignedAgent ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.estimate ? count.format(row.estimate.inputTokens + row.estimate.outputTokens) : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.estimate ? usd.format(row.estimate.estimatedCost) : '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{count.format(row.actual.totalTokens)}</td>
              <td className="px-4 py-2 text-right tabular-nums font-medium">{usd.format(row.actual.costUsd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IntakeCard({ status }: { status: IntakeStatus }) {
  const { intake, requirements, estimated, actual } = status;
  const live = requirements.filter((r) => !r.withdrawn);
  const done = live.filter((r) => r.status === 'done' || r.status === 'closed').length;
  return (
    <section className="space-y-4" aria-label={`Intake ${intake.number}`}>
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-black dark:text-white">{intake.number} · {intake.title}</h3>
          <span className="text-[11px] text-gray-500">Revision {intake.revision} · received {new Date(intake.updatedAt).toLocaleString()}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs mt-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Customer</div>
            <div className="mt-1 font-medium text-black dark:text-white">{intake.customerName}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Approved by</div>
            <div className="mt-1 font-medium text-black dark:text-white">{intake.approvedBy ?? 'Not recorded'}</div>
            {intake.approvedOn && <div className="text-[11px] text-gray-500">{intake.approvedOn}</div>}
          </div>
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Stakeholders</div>
            <div className="mt-1 text-black dark:text-white">
              {intake.stakeholders.length === 0 ? '—' : intake.stakeholders.map((s) => `${s.name}${s.role ? ` (${ROLE_LABELS[s.role] ?? s.role})` : ''}`).join(', ')}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Figure label="Requirements" value={count.format(live.length)} note={`${count.format(done)} done${requirements.length > live.length ? ` · ${requirements.length - live.length} withdrawn` : ''}`} />
        <Figure label="Estimated tokens" value={count.format(estimated.totalTokens)} note={`${count.format(estimated.requirements)} estimated`} />
        <Figure label="Estimated cost" value={usd.format(estimated.costUsd)} note="At the configured model rates" />
        <Figure label="Actual cost so far" value={usd.format(actual.costUsd)} note={`${count.format(actual.sessions)} agent sessions on these requirements`} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
        <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800 text-xs font-semibold text-black dark:text-white">Requirements: estimate against actual</div>
        <RequirementsTable rows={requirements} />
      </div>

      {intake.sections.map((section) => (
        <details key={section.key} className="bg-white border border-zinc-200 rounded-lg dark:bg-zinc-900 dark:border-zinc-800">
          <summary className="px-4 py-3 text-xs font-semibold text-black dark:text-white cursor-pointer">
            {section.title} <span className="font-normal text-gray-500">· {section.answers.filter((a) => a.answer).length} of {section.answers.length} answered</span>
          </summary>
          <dl className="px-4 pb-4 space-y-3 text-xs">
            {section.answers.map((a) => (
              <div key={a.key}>
                <dt className="text-gray-500">{a.question}</dt>
                <dd className="mt-1 text-black dark:text-white whitespace-pre-wrap">{a.answer || <span className="text-gray-400">Not answered</span>}</dd>
                {a.answer && a.answeredBy && <dd className="text-[11px] text-gray-500">Answered by {a.answeredBy}</dd>}
              </div>
            ))}
          </dl>
        </details>
      ))}
    </section>
  );
}

export function IntakeTab({ projectId }: IntakeTabProps) {
  const [intakes, setIntakes] = useState<IntakeStatus[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getProjectIntakes(projectId);
        if (!cancelled) setIntakes(data);
      } catch (err) {
        console.error('[IntakeTab] Failed to load intakes:', err);
        if (!cancelled) setError('The intake could not be loaded. Try again in a moment.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [projectId]);

  if (isLoading) return <LoadingSpinner />;
  if (error || !intakes) {
    return <EmptyState title="Intake unavailable" description={error ?? 'Nothing came back for this project.'} />;
  }
  if (intakes.length === 0) {
    return (
      <EmptyState
        title="No requirements intake"
        description="No intake has been sent from the CRM for this project. An intake is run in the CRM with the customer's stakeholders; once it is approved there, its requirements arrive here as backlog items with estimates."
      />
    );
  }

  return (
    <div className="space-y-8">
      {intakes.map((status) => <IntakeCard key={status.intake.externalRef} status={status} />)}
      <div className="text-[11px] text-gray-500">
        This is a copy of what the CRM sent. To change a requirement, change it in the CRM and submit the intake again: an item nobody has started follows the change, and work already in hand is left as it is.
      </div>
    </div>
  );
}
